import { Project } from "@/store/builderSlice";
import { getUserId } from "@/utils/builder/userUtils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from 'axios'
const axiosInstance = axios.create({
    baseURL: "https://d221a61rb87vel.cloudfront.net/v1",
})
const axiosInstanceApiNestingaleV1 = axios.create({
    baseURL: "https://api.nestingale.com/v1",
})
type RoleConfig = {
    api: any;
    resourcePath: string;
    transformPayload?: (project: Project) => any;
    headerKey:any;
};

const roleConfigMap: Record<string, RoleConfig> = {
    default: {
        api: axiosInstance,
        resourcePath: "projects",
        headerKey:'Customer-Id'
    },
    interiorDesigner: {
        api: axiosInstanceApiNestingaleV1,
        resourcePath: "layoutTemplates",
        transformPayload: (rawProject: Project) => {
            const productsMetaData = (rawProject.products || []).flatMap((product: any) => {
                const metadata: string[] = [];
        
                if (product.productTypeName) metadata.push(product.productTypeName);
                if (product.productSubTypeName) metadata.push(product.productSubTypeName);
        
                const dimensions = product.productDimentions;
                if (dimensions) {
                    const parts: string[] = [];
                    if (dimensions.height) parts.push(`height${dimensions.height}`);
                    if (dimensions.width) parts.push(`width${dimensions.width}`);
                    if (dimensions.depth) parts.push(`depth${dimensions.depth}`);
                    
                    if (parts.length > 0) {
                        metadata.push(parts.join("-")); // e.g., "height40-width49", "width49-depth30", etc.
                    }
                }
        
                return metadata;
            });
            return {
                layoutTemplateID: rawProject.projectId,
                layoutTemplateName: rawProject.projectName,
                surfaceGroups: rawProject.surfaceGroups,
                metaData: {
                    ...rawProject.metaData,
                    productsMetaData: Array.from(new Set(productsMetaData))
                },
                products: rawProject.products,
                nonSellableProducts: (rawProject.products || []).filter(
                    (p: any) => p.isSellable === false
                ),
                roomModel: rawProject.roomModel,
                designerId: getUserId(),
            }
        },
        headerKey:'Designer-Id'
    }
};

export const saveProject = createAsyncThunk(
    "builder/saveProject",
    async (
        config: { currentRole?: string },
        { rejectWithValue, getState }
    ) => {
        const role = config?.currentRole || "default";
        const roleConfig = roleConfigMap[role] || roleConfigMap["default"];

        try {
            const rawProject: Project = Object.assign({}, (getState() as any).builder.project);
            const projectId = rawProject.projectId;

            let url = `/api/${roleConfig.resourcePath}`;
            let method: "post" | "put" = "post";

            if (projectId) {
                url += `/${projectId}`;
                method = "put";
                delete rawProject.projectId;
            }

            const transformedPayload = roleConfig.transformPayload
                ? roleConfig.transformPayload(rawProject)
                : rawProject;
            //need to change headers
            const result = await roleConfig.api[method](url, transformedPayload, {
                headers: {
                    [roleConfig['headerKey']]: getUserId()
                }
            }).then(
                (response: any) => response.data,
                (err: any) => {
                    const errorObj = {
                        description: "NEST_SPA:Slices:saveProject:Exception",
                        errorCode: "NEST_SPA:BUILDER_PAGE:SAVE_PROJECT",
                        ...err,
                    };
                    console.log(errorObj);
                    throw errorObj;
                }
            );

            console.log("Project saved :: ", result);
            return projectId || result.projectId || result.layoutTemplateId
        } catch (err) {
            console.log({
                description: "NEST_SPA:Slices:saveProject:Exception",
                errorCode: "NEST_SPA:BUILDER_PAGE:SAVE_PROJECT",
                err_desc: (err as Error).message,
                err_stack: (err as Error).stack,
            });
            return rejectWithValue(err);
        }
    }
);


export const loadProject = async (config: { projectId: string, currentRole?: string }) => {
    const { projectId, currentRole = 'default' } = config;
    const roleConfig = roleConfigMap[currentRole] || roleConfigMap["default"];
    const url = `/api/${roleConfig.resourcePath}/${projectId}`;
    //need to change headers
    return await roleConfig.api.get(url,{
        headers: {
            [roleConfig['headerKey']]: getUserId()
        }
    })
        .then((response: any) => {
            const data = response.data;

            // Normalize layoutTemplate fields to match project structure
            if (currentRole === 'interiorDesigner') {
                return {
                    ...data,
                    projectId: data.layoutTemplateId,
                    projectName: data.layoutTemplateName,
                };
            }

            return data;
        }, (err:any) => {
            const errorObj = Object.assign(
                {
                    description: "NEST_SPA::getBuilderProductTypeData:Exception",
                    errorCode: "NEST_SPA:BUILDER_PAGE",
                },
                err
            );
            throw errorObj;
        });
};
